/**
 * Consolidated Chat Store for Atom Assist
 *
 * This store follows Zustand best practices:
 * - Single source of truth for all chat-related state
 * - Clear domain boundaries (chat config, dialogue, datasets, streaming)
 * - Computed selectors instead of duplicate state
 * - No refs for state management
 * - Persistence via middleware where appropriate
 *
 * @see /home/nuclearn/frontend/.github/copilot-instructions.md
 * @see /home/nuclearn/frontend/ATOM_ASSIST_ZUSTAND_REVIEW.md
 */

import {
  createJSONStorage,
  persist,
  subscribeWithSelector,
} from "zustand/middleware";

import {
  ChatConfig,
  DEFAULT_RESPONSE_MODE,
  ResponseMode,
} from "src/reusable/Chat/types/chatTypes";
import { FileUploadState } from "src/reusable/types/fileUploadWithProgress";
import {
  AgentDatasetAccessState,
  ChatMessageWithParts,
  ImageContent,
} from "src/views/assistant/types";
import { RoutingMetadata } from "src/views/assistant/routing/types";
import { create } from "zustand";
import { determinePlatformType } from "src/reusable/Functions";

// ===========================
// Types
// ===========================

/**
 * Builds the chat URL for a dialogue. May be async to fetch additional route
 * params (e.g. a run id) before returning. If no builder is registered for a
 * product, RecentChats/AllChats fall back to `${basePath}/chat/${dialogueId}`.
 * `kind` is the dialogue's own kind when known (e.g. Parts AI's
 * run/comparison/search), so a builder serving a mixed-kind list can route
 * each row correctly without assuming a single fixed kind for the page.
 */
export type BuildChatUrlFn = (
  dialogueId: number,
  kind?: string | null,
) => string | Promise<string>;

export interface AssistantProductConfig {
  productKey: string;
  productName: string;
  basePath: string;
}

export interface PersonaData {
  id?: number;
  name: string;
  description?: string;
  slug?: string;
  initialPrompt?: string;
  systemPrompt?: string;
  datasets?: any[];
  config?: any;
  /** Model capabilities from inference_data_config */
  modelCapabilities?: {
    supportsVision?: boolean;
    supportsReasoning?: boolean;
    supportsFunctionCalling?: boolean;
  };
}

/**
 * Workflow persona data from sidebar navigation (Issue #1925)
 */
/**
 * A snapshot of the composer's config and persona, taken when navigateToChat
 * hands them to `dialogueId`. A snapshot, not the live config: opening
 * another dialogue or editing the splash composer replaces the live one.
 */
export interface ComposerHandoff {
  dialogueId: number;
  config: ChatConfig;
  persona: PersonaData | null;
}

export interface WorkflowPersonaData {
  id: number;
  slug: string;
  name: string;
  introduction_prompt: string | null;
  required_product: string | null;
  access_granted: boolean;
  config_required: boolean;
  config_completed: boolean;
  unbound_slots: string[];
}

export interface Dataset {
  id: number;
  name: string;
  [key: string]: any;
}

export interface Artifact {
  id: string;
  type: string;
  content: string;
  metadata?: any;
  [key: string]: any;
}

export interface StreamingState {
  isConnected: boolean;
  isLoading: boolean;
  isEnded: boolean;
  error: string | null;
  responseSources: any[];
}

// ===========================
// Active Persona Identity (derived from routing > persona > none)
// ===========================

export interface ActivePersonaIdentity {
  id?: number;
  slug: string;
  name: string;
  description?: string;
  source: "routing" | "manual" | "none";
  accessDenied?: boolean;
}

// ===========================
// Chat Store State Interface
// ===========================

export interface ChatStoreState {
  // ========== Product Configuration (persists in sessionStorage) ==========
  productConfig: AssistantProductConfig;

  // Per-product chat-URL builders, keyed by productKey. Lives outside
  // productConfig (and is NOT persisted — functions don't survive JSON) so a
  // builder registered by one product can never be invoked against another
  // product's dialogue: RecentChats/AllChats look up the builder by the
  // product whose dialogue is being opened, not whichever product registered
  // last. See registerChatUrlBuilder.
  buildChatUrlRegistry: Record<string, BuildChatUrlFn>;

  // ========== Chat Configuration (persists in sessionStorage) ==========
  config: ChatConfig | null;
  persona: PersonaData | null;

  // ========== Navigation State ==========
  hasTalkedFirst: boolean; // Prevents auto-start dialogue from triggering multiple times
  initialUserMessage: string | undefined; // Message to send when chat page loads (from splash)
  initialImages: ImageContent[] | undefined; // VLM images to send with initial message (from splash)
  initialSendId: string | undefined; // send_id minted at splash submit — pairs ai_message_sent with ai_response_completed (#29704)

  // ========== Workflow Navigation State (Issue #1925) ==========
  forceAutoStart: boolean; // Forces auto-start behavior when navigating from workflow sidebar
  workflowPersona: WorkflowPersonaData | null; // Persona data from workflow navigation

  // ========== Active Dialogue (in-memory, resets on navigation) ==========
  activeDialogueId: number | null;
  /** The composer's pick as navigateToChat handed it to a dialogue; wins over its record until the first turn. */
  composerHandoff: ComposerHandoff | null;
  dialogue: ChatMessageWithParts[];
  artifacts: Record<string, Artifact>;

  // ========== Dataset Management (computed from multiple sources) ==========
  // Project datasets (from project's dataset folder)
  projectDatasets: Dataset[];

  // Dropdown-selected datasets (from chat config UI)
  dropdownDatasets: Dataset[];

  // Ad-hoc uploaded files (uploaded during chat)
  uploadedFiles: FileUploadState[];

  // ========== Streaming State (ephemeral, resets on connection) ==========
  streaming: StreamingState;

  // ========== Document-scoped Search (for "Chat with Document" feature) ==========
  sourceDataRecordIds: number[];

  // ========== Default Model Capabilities (from GeneralLLM, fetched on app load) ==========
  defaultSupportsVision: boolean | null; // null = not yet loaded, true/false = loaded

  // ========== Routing State (ephemeral, from SSE metadata) ==========
  routing: RoutingMetadata | null;
  /** Set while the active agent's datasets are filtered for this user (#30968). */
  agentDatasetAccess: AgentDatasetAccessState | null;

  // ========== Response Mode ==========
  /**
   * Thorough/Quick toggle on the AtomAssist composer. A property of the
   * conversation: editable until the first message is submitted, then locked
   * for that dialogue. Lives outside `config` because persona selection
   * replaces `config` wholesale. Not persisted — a loaded dialogue restores
   * it from its own config, a fresh splash starts at Thorough.
   */
  responseMode: ResponseMode;
  /** True once the active conversation has a submitted message. */
  responseModeLocked: boolean;

  // ========== Computed Selectors ==========
  /**
   * Get all active datasets (project + dropdown + uploaded)
   * This is the single source of truth for dataset IDs to send with messages
   */
  getActiveDatasets: () => Dataset[];

  /**
   * Get active dataset IDs only
   */
  getActiveDatasetIds: () => number[];

  /**
   * Check if chat is ready to send messages
   */
  isChatReady: () => boolean;

  /**
   * Get active source data record IDs for document-scoped search
   */
  getSourceDataRecordIds: () => number[];

  /**
   * Check if current model supports vision/image inputs
   * Returns true by default if no model info is available (allow pasting, let backend handle errors)
   */
  getSupportsVision: () => boolean;

  /**
   * Get the active persona identity with strict priority: routing > persona > none.
   * Single source of truth for display name across all components.
   */
  getActivePersonaIdentity: () => ActivePersonaIdentity;

  // ========== Actions: Configuration ==========
  setConfig: (config: ChatConfig | null) => void;
  setPersona: (persona: PersonaData | null) => void;
  setActiveDialogueId: (id: number | null) => void;
  setComposerHandoff: (handoff: ComposerHandoff | null) => void;
  setProductConfig: (config: AssistantProductConfig) => void;
  registerChatUrlBuilder: (productKey: string, builder: BuildChatUrlFn) => void;

  // Granular config updates
  setModelId: (modelId: number) => void;
  setModelVersionNum: (versionNum: number) => void;
  setTopSearchResults: (numResults: number) => void;
  setDatasets: (datasets: any[]) => void;
  setTemperature: (temperature: number) => void;
  setEnableThinking: (enableThinking: boolean) => void;

  // ========== Actions: Dialogue ==========
  setDialogue: (dialogue: ChatMessageWithParts[]) => void;
  addMessage: (message: ChatMessageWithParts) => void;
  updateMessage: (
    messageId: string,
    updates: Partial<ChatMessageWithParts>,
  ) => void;
  clearDialogue: () => void;

  // ========== Actions: Artifacts ==========
  addArtifact: (artifact: Artifact) => void;
  updateArtifact: (artifactId: string, updates: Partial<Artifact>) => void;
  removeArtifact: (artifactId: string) => void;
  clearArtifacts: () => void;

  // ========== Actions: Datasets ==========
  setProjectDatasets: (datasets: Dataset[]) => void;
  setDropdownDatasets: (datasets: Dataset[]) => void;
  setSourceDataRecordIds: (sdrIds: number[]) => void;
  setDefaultSupportsVision: (supportsVision: boolean) => void;
  setPrivateDatasetDocumentsAsAttachments: (documents: any[]) => Promise<void>;
  addUploadedFile: (file: FileUploadState) => void;
  removeUploadedFile: (fileId: string) => void;
  updateUploadedFile: (
    fileId: string,
    updates: Partial<FileUploadState>,
  ) => void;
  clearUploadedFiles: () => void;

  // ========== Actions: Navigation ==========
  setHasTalkedFirst: (hasTalked: boolean) => void;
  setInitialUserMessage: (message: string | undefined) => void;
  setInitialImages: (images: ImageContent[] | undefined) => void;
  setInitialSendId: (sendId: string | undefined) => void;

  // ========== Actions: Workflow Navigation (Issue #1925) ==========
  setForceAutoStart: (force: boolean) => void;
  setPersonaForWorkflow: (workflowPersona: WorkflowPersonaData | null) => void;

  // ========== Actions: Streaming ==========
  setStreamingState: (updates: Partial<StreamingState>) => void;
  setResponseSources: (sources: any[]) => void;

  // ========== Actions: Routing ==========
  setRouting: (routing: RoutingMetadata | null) => void;
  setAgentDatasetAccess: (access: AgentDatasetAccessState | null) => void;

  // ========== Actions: Response Mode ==========
  /** No-op while locked: the mode cannot change once a conversation started. */
  setResponseMode: (mode: ResponseMode) => void;
  /** Fix the mode for the active conversation (call the moment a message is submitted). */
  lockResponseMode: () => void;
  /** New conversation: unlock and return to the Thorough default. */
  resetResponseMode: () => void;
  /** Loaded conversation: adopt its persisted mode and lock (bypasses the lock guard). */
  restoreResponseMode: (mode: ResponseMode) => void;

  // ========== Actions: Lifecycle ==========
  /**
   * Reset dialogue and artifacts (when navigating between conversations)
   */
  resetDialogue: () => void;

  /**
   * Reset to base config (clear persona-specific settings)
   */
  resetToBaseConfig: () => void;

  /**
   * Reset entire store (logout, clear all data)
   */
  resetAll: () => void;
}

// ===========================
// Initial State
// ===========================

const getDefaultProductConfig = (): AssistantProductConfig => ({
  productKey: "atom-assist",
  productName: determinePlatformType(),
  basePath: "/assistant",
});

const getInitialState = () => ({
  // Product config
  productConfig: getDefaultProductConfig(),
  buildChatUrlRegistry: {} as Record<string, BuildChatUrlFn>,

  // Config
  config: null,
  persona: null,

  // Navigation
  hasTalkedFirst: false,
  initialUserMessage: undefined,
  initialImages: undefined,
  initialSendId: undefined,

  // Workflow Navigation (Issue #1925)
  forceAutoStart: false,
  workflowPersona: null,

  // Dialogue
  activeDialogueId: null,
  composerHandoff: null,
  dialogue: [],
  artifacts: {},

  // Datasets
  projectDatasets: [],
  dropdownDatasets: [],
  uploadedFiles: [],
  sourceDataRecordIds: [],

  // Default model capabilities (fetched from backend)
  defaultSupportsVision: null, // null = not yet loaded

  // Routing + dataset-access banner (both persisted, see partialize)
  routing: null,
  agentDatasetAccess: null,

  // Thorough is the default; the user opts into Quick per conversation.
  responseMode: DEFAULT_RESPONSE_MODE,
  responseModeLocked: false,

  // Streaming
  streaming: {
    isConnected: false,
    isLoading: false,
    isEnded: true,
    error: null,
    responseSources: [],
  },
});

// ===========================
// Store Implementation
// ===========================

export const useChatStore = create<ChatStoreState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Initialize state
        ...getInitialState(),

        // ========== Computed Selectors ==========

        getActiveDatasets: () => {
          const state = get();
          const allDatasets: Dataset[] = [];

          // Add project datasets
          allDatasets.push(...state.projectDatasets);

          // Add dropdown datasets (only if not already in project datasets)
          const projectIds = new Set(
            state.projectDatasets.map((d: any) => d.id),
          );
          state.dropdownDatasets.forEach((dataset: any) => {
            if (!projectIds.has(dataset.id)) {
              allDatasets.push(dataset);
            }
          });

          // Add uploaded file datasets (only when NOT in document-scoped mode)
          // When sourceDataRecordIds is set, we're chatting with specific documents,
          // not the whole dataset - so don't show the parent dataset as "active"
          if (state.sourceDataRecordIds.length === 0) {
            state.uploadedFiles.forEach((file: any) => {
              if (file.datasetId && file.uploadStatus === "uploaded") {
                // Check if not already added
                if (!allDatasets.some((d) => d.id === file.datasetId)) {
                  allDatasets.push({
                    id: file.datasetId,
                    name: file.file?.name || "Unknown",
                    sourceDataRecordId: file.sourceDataRecordId,
                  });
                }
              }
            });
          }

          return allDatasets;
        },

        getActiveDatasetIds: () => {
          return get()
            .getActiveDatasets()
            .map((d: any) => d.id);
        },

        isChatReady: () => {
          const state = get();
          // modelId can be null - backend will default to GeneralLLM
          return !!state.config;
        },

        getSourceDataRecordIds: () => {
          return get().sourceDataRecordIds;
        },

        getSupportsVision: () => {
          const state = get();
          // Check persona modelCapabilities first
          if (state.persona?.modelCapabilities?.supportsVision !== undefined) {
            return state.persona.modelCapabilities.supportsVision;
          }
          // Check config modelCapabilities
          if (state.config?.modelCapabilities?.supportsVision !== undefined) {
            return state.config.modelCapabilities.supportsVision;
          }
          // Fall back to default model capabilities (GeneralLLM) if fetched
          if (state.defaultSupportsVision !== null) {
            return state.defaultSupportsVision;
          }
          // Default to false if no model info available - block images unless we know model supports vision
          return false;
        },

        getActivePersonaIdentity: (): ActivePersonaIdentity => {
          const state = get();
          // Priority 1: Routing (SSE-assigned persona)
          if (state.routing?.persona_slug) {
            return {
              slug: state.routing.persona_slug,
              name: state.routing.persona_name || state.routing.persona_slug,
              source: "routing",
              accessDenied: state.routing.access_denied,
            };
          }
          // Priority 2: Manually selected or resolved persona
          if (state.persona?.slug) {
            return {
              id: state.persona.id,
              slug: state.persona.slug,
              name: state.persona.name,
              description: state.persona.description,
              source: "manual",
            };
          }
          // Priority 3: No persona
          return { slug: "", name: "", source: "none" };
        },

        // ========== Actions: Configuration ==========

        setConfig: (config: any) => {
          set({
            config,
            // Sync datasets from config to dropdownDatasets to maintain consistency
            // This ensures that when persona config is loaded, datasets appear in dropdown
            dropdownDatasets: config?.datasets || [],
          });
        },

        setPersona: (persona: any) => {
          set({ persona });
        },

        setActiveDialogueId: (id: any) => {
          set({ activeDialogueId: id });
        },

        setComposerHandoff: (handoff: ComposerHandoff | null) => {
          set({ composerHandoff: handoff });
        },

        setProductConfig: (productConfig: AssistantProductConfig) => {
          set({ productConfig });
        },

        registerChatUrlBuilder: (
          productKey: string,
          builder: BuildChatUrlFn,
        ) => {
          set((state: ChatStoreState) => ({
            buildChatUrlRegistry: {
              ...state.buildChatUrlRegistry,
              [productKey]: builder,
            },
          }));
        },

        setModelId: (modelId: any) => {
          set((state: any) => ({
            config: state.config ? { ...state.config, modelId } : null,
          }));
        },

        setModelVersionNum: (modelVersionNum: any) => {
          set((state: any) => ({
            config: state.config ? { ...state.config, modelVersionNum } : null,
          }));
        },

        setTopSearchResults: (topSearchResults: any) => {
          set((state: any) => ({
            config: state.config ? { ...state.config, topSearchResults } : null,
          }));
        },

        setDatasets: (datasets: any[]) => {
          set((state: any) => ({
            config: state.config ? { ...state.config, datasets } : null,
            // Also update dropdownDatasets to keep them in sync
            // This ensures useActiveDatasets() reflects the current selection
            dropdownDatasets: datasets || [],
          }));
        },

        setTemperature: (temperature: number) => {
          set((state: any) => ({
            config: state.config
              ? {
                  ...state.config,
                  chatControlMetadata: {
                    ...state.config.chatControlMetadata,
                    temperature,
                  },
                }
              : null,
          }));
        },

        setEnableThinking: (enableThinking: boolean) => {
          set((state: any) => ({
            config: state.config
              ? {
                  ...state.config,
                  chatControlMetadata: {
                    ...state.config.chatControlMetadata,
                    enable_thinking: enableThinking,
                  },
                }
              : null,
          }));
        },

        // ========== Actions: Dialogue ==========

        setDialogue: (dialogue: any) => {
          set({ dialogue });
        },

        addMessage: (message: any) => {
          set((state: any) => ({
            dialogue: [...state.dialogue, message],
          }));
        },

        updateMessage: (messageId: any, updates: any) => {
          set((state: any) => ({
            dialogue: state.dialogue.map((msg: any) =>
              msg.id === messageId ? { ...msg, ...updates } : msg,
            ),
          }));
        },

        clearDialogue: () => {
          set({ dialogue: [] });
        },

        // ========== Actions: Artifacts ==========

        addArtifact: (artifact: any) => {
          set((state: any) => ({
            artifacts: {
              ...state.artifacts,
              [artifact.id]: artifact,
            },
          }));
        },

        updateArtifact: (artifactId: any, updates: any) => {
          set((state: any) => ({
            artifacts: {
              ...state.artifacts,
              [artifactId]: {
                ...state.artifacts[artifactId],
                ...updates,
              },
            },
          }));
        },

        removeArtifact: (artifactId: any) => {
          set((state: any) => {
            const { [artifactId]: removed, ...rest } = state.artifacts;
            return { artifacts: rest };
          });
        },

        clearArtifacts: () => {
          set({ artifacts: {} });
        },

        // ========== Actions: Datasets ==========

        setProjectDatasets: (projectDatasets: any[]) => {
          set({ projectDatasets });
        },

        setDropdownDatasets: (dropdownDatasets: any[]) => {
          set((state: any) => ({
            dropdownDatasets,
            // Also update config.datasets to keep them in sync
            config: state.config
              ? { ...state.config, datasets: dropdownDatasets }
              : null,
          }));
        },

        setSourceDataRecordIds: (sourceDataRecordIds: number[]) => {
          set({ sourceDataRecordIds });
        },

        setDefaultSupportsVision: (supportsVision: boolean) => {
          set({ defaultSupportsVision: supportsVision });
        },

        setPrivateDatasetDocumentsAsAttachments: async (documents: any[]) => {
          // Convert individual documents from private datasets to attachment format
          const attachments: FileUploadState[] = documents.map((doc) => {
            // Extract and clean file name from the document
            let fileName =
              doc.artifact_name ||
              doc.file_name ||
              doc.name ||
              doc.filename ||
              "Untitled Document";

            // Remove dataset_files/ prefix and dataset_id_XXXX_ pattern for cleaner display
            fileName = fileName
              .replace(/^dataset_files\//, "") // Remove dataset_files/ prefix
              .replace(/dataset_id_\d+_/, ""); // Remove dataset_id_XXXX_ pattern

            // Determine file type from extension or metadata
            const fileExtension =
              fileName.split(".").pop()?.toLowerCase() || "";
            const mimeType =
              doc.mime_type || doc.file_type || `application/${fileExtension}`;

            // Get the SDR ID (should be already populated from useLoadDialogue)
            const sourceDataRecordId = doc.source_data_record_id || doc.sdr_id;

            // Also attach SDR ID to the File object for component access
            const fileWithSdr = {
              name: fileName,
              size: doc.file_size || 0,
              type: mimeType,
              sourceDataRecordId: sourceDataRecordId,
              datasetId: doc.dataset_id,
            } as any;

            return {
              id: `artifact-${doc.id || doc.artifact_id}`,
              file: fileWithSdr as File,
              datasetId: doc.dataset_id,
              artifactId: doc.id || doc.artifact_id,
              uploadStatus: "uploaded" as const,
              uploadProgress: 100,
              processingStatus: "completed" as const,
              processingProgress: 100,
              sourceDataRecordId: sourceDataRecordId,
              // Mark as a pre-loaded document from dialogue history
              isFromDialogueHistory: true,
              // Preserve the project flag if it exists
              isFromProject: doc.isFromProject || false,
            };
          });

          console.log(
            "📄 ChatStore.setPrivateDatasetDocumentsAsAttachments - Converting",
            documents.length,
            "documents to attachments",
          );

          set((state: any) => {
            // Check for duplicates before adding based on artifact ID or source data record ID
            const existingArtifactIds = new Set(
              state.uploadedFiles
                .filter((f: any) => f.artifactId)
                .map((f: any) => String(f.artifactId)),
            );
            const existingSdrIds = new Set(
              state.uploadedFiles
                .filter((f: any) => f.sourceDataRecordId)
                .map((f: any) => String(f.sourceDataRecordId)),
            );

            // Also check by file ID pattern for artifact-based IDs
            const existingFileIds = new Set(
              state.uploadedFiles.map((f: any) => f.id),
            );

            const newAttachments = attachments.filter(
              (attachment: FileUploadState) => {
                // Skip if file ID already exists (e.g., artifact-123)
                if (existingFileIds.has(attachment.id)) {
                  console.log(
                    "📄 Skipping duplicate file by ID:",
                    attachment.id,
                  );
                  return false;
                }

                // Skip if artifact already exists
                if (
                  attachment.artifactId &&
                  existingArtifactIds.has(String(attachment.artifactId))
                ) {
                  console.log(
                    "📄 Skipping duplicate artifact:",
                    attachment.artifactId,
                  );
                  return false;
                }
                // Skip if SDR already exists
                if (
                  attachment.sourceDataRecordId &&
                  existingSdrIds.has(String(attachment.sourceDataRecordId))
                ) {
                  console.log(
                    "📄 Skipping duplicate SDR:",
                    attachment.sourceDataRecordId,
                  );
                  return false;
                }
                return true;
              },
            );

            console.log(
              "📄 ChatStore.setPrivateDatasetDocumentsAsAttachments - Adding",
              newAttachments.length,
              "new attachments (filtered from",
              attachments.length,
              "total)",
              {
                existingCount: state.uploadedFiles.length,
                existingArtifactIds: existingArtifactIds.size,
                existingSdrIds: existingSdrIds.size,
              },
            );

            return {
              uploadedFiles: [...state.uploadedFiles, ...newAttachments],
            };
          });
        },

        addUploadedFile: (file: any) => {
          set((state: any) => {
            // Check for duplicates before adding
            const exists = state.uploadedFiles.some((f: any) => {
              // Check by ID
              if (f.id === file.id) return true;

              // Check by sourceDataRecordId only if both have it
              if (
                f.sourceDataRecordId &&
                file.sourceDataRecordId &&
                f.sourceDataRecordId === file.sourceDataRecordId
              ) {
                return true;
              }

              // Check by datasetId and filename for project files
              if (
                f.datasetId &&
                file.datasetId &&
                f.datasetId === file.datasetId &&
                f.file?.name === file.file?.name
              ) {
                return true;
              }

              return false;
            });

            if (exists) {
              return state;
            }

            return {
              uploadedFiles: [...state.uploadedFiles, file],
            };
          });
        },

        removeUploadedFile: (fileId: any) => {
          set((state: any) => ({
            uploadedFiles: state.uploadedFiles.filter(
              (f: any) => f.id !== fileId,
            ),
          }));
        },

        updateUploadedFile: (fileId: any, updates: any) => {
          set((state: any) => ({
            uploadedFiles: state.uploadedFiles.map((f: any) =>
              f.id === fileId ? { ...f, ...updates } : f,
            ),
          }));
        },

        clearUploadedFiles: () => {
          // Clear all uploaded files
          set({ uploadedFiles: [] });
        },

        // ========== Actions: Navigation ==========

        setHasTalkedFirst: (hasTalked: any) => {
          set({ hasTalkedFirst: hasTalked });
        },

        setInitialUserMessage: (message: any) => {
          set({ initialUserMessage: message });
        },

        setInitialImages: (images: ImageContent[] | undefined) => {
          set({ initialImages: images });
        },

        setInitialSendId: (sendId: string | undefined) => {
          set({ initialSendId: sendId });
        },

        // ========== Actions: Workflow Navigation (Issue #1925) ==========

        setForceAutoStart: (force: boolean) => {
          set({ forceAutoStart: force });
        },

        setPersonaForWorkflow: (
          workflowPersona: WorkflowPersonaData | null,
        ) => {
          set({ workflowPersona });
        },

        // ========== Actions: Streaming ==========

        setStreamingState: (updates: any) => {
          set((state: any) => ({
            streaming: {
              ...state.streaming,
              ...updates,
            },
          }));
        },

        setResponseSources: (sources: any[]) => {
          set((state: any) => {
            const existing: any[] = state.streaming.responseSources || [];
            // Dedup against existing AND within the incoming payload itself.
            // Search occasionally returns the same data_chunk.id twice in one
            // tool call — without intra-payload dedup, rehypeWrapCite's Map
            // silently overwrites with the LAST occurrence's index while
            // SourcesChunkView renders the FIRST, so citations scroll to the
            // wrong card.
            const seenIds = new Set(
              existing
                .map((s: any) => s.data_chunk?.id)
                .filter((id: any) => id != null),
            );
            const newSources = sources.filter((s: any) => {
              const id = s.data_chunk?.id;
              if (id == null) return true;
              if (seenIds.has(id)) return false;
              seenIds.add(id);
              return true;
            });
            return {
              streaming: {
                ...state.streaming,
                responseSources: [...existing, ...newSources],
              },
            };
          });
        },

        // ========== Actions: Routing ==========

        setRouting: (routing: RoutingMetadata | null) => {
          set({ routing });
        },

        setAgentDatasetAccess: (access: AgentDatasetAccessState | null) => {
          set({ agentDatasetAccess: access });
        },

        // ========== Actions: Response Mode ==========

        setResponseMode: (mode: ResponseMode) => {
          if (get().responseModeLocked) return;
          set({ responseMode: mode });
        },

        lockResponseMode: () => {
          set({ responseModeLocked: true });
        },

        resetResponseMode: () => {
          set({
            responseMode: DEFAULT_RESPONSE_MODE,
            responseModeLocked: false,
          });
        },

        restoreResponseMode: (mode: ResponseMode) => {
          set({ responseMode: mode, responseModeLocked: true });
        },

        // ========== Actions: Lifecycle ==========

        resetDialogue: () => {
          set({
            dialogue: [],
            artifacts: {},
            routing: null,
            agentDatasetAccess: null,
            // Don't clear uploadedFiles - they should persist across dialogue resets
            // uploadedFiles: [], // REMOVED to preserve files during navigation
            // Clear document-scoped search when starting new dialogue
            sourceDataRecordIds: [],
            streaming: {
              isConnected: false,
              isLoading: false,
              isEnded: true,
              error: null,
              responseSources: [],
            },
          });
        },

        resetToBaseConfig: () => {
          // Create minimal config for chat - modelId should be null (backend defaults to GeneralLLM)
          // Don't preserve old modelId - it may reference a deleted model or be from a detached persona
          // Identity fields (name, slug) belong in PersonaData, not ChatConfig
          set({
            config: {
              modelId: null,
              modelVersionNum: null,
              datasets: [],
              chatControlMetadata: {},
              topSearchResults: 5,
              toolCalling: true,
            },
            persona: null,
            dropdownDatasets: [],
          });
        },

        resetAll: () => {
          set(getInitialState());
        },
      }),
      {
        name: "atom-assist-chat",
        storage: createJSONStorage(() => sessionStorage),
        // Only persist configuration and persona, not dialogue/artifacts
        partialize: (state) => ({
          productConfig: state.productConfig,
          config: state.config,
          persona: state.persona,
          activeDialogueId: state.activeDialogueId,
          // A reload before the first turn must still keep the composer's pick.
          composerHandoff: state.composerHandoff,
          // Persist routing metadata so auto-routed state survives refresh
          routing: state.routing,
          agentDatasetAccess: state.agentDatasetAccess,
          // Also persist dropdownDatasets to maintain state across reloads
          dropdownDatasets: state.dropdownDatasets,
          // Persist initial message and images for splash -> chat navigation.
          // NEVER persist the empty-string "talk-first" sentinel: that's a
          // transient signal to useStartChatOnMount, and if it leaks across
          // page loads it will cause an unintended emptySubmission() — which
          // shows up as the assistant talking first in non-FTUX chats for
          // users who haven't toggled auto_start.
          initialUserMessage: state.initialUserMessage || undefined,
          initialImages: state.initialImages,
          // Only meaningful alongside a pending initial submission — which
          // can be images-only with empty text, so check both.
          initialSendId:
            state.initialUserMessage || state.initialImages?.length
              ? state.initialSendId
              : undefined,
          // Persist metadata of successfully uploaded files (those with datasetIds)
          // We can't persist File objects, but we can persist the metadata
          // uploadedFilesMetadata: state.uploadedFiles
          //   .filter(
          //     (file: any) => file.datasetId && file.uploadStatus === "uploaded"
          //   )
          //   .map((file: any) => ({
          //     id: file.id,
          //     fileName: file.file?.name || "Unknown",
          //     datasetId: file.datasetId,
          //     sourceDataRecordId: file.sourceDataRecordId,
          //     uploadStatus: file.uploadStatus,
          //     processingStatus: file.processingStatus,
          //     isFromProject: (file as any).isFromProject,
          //     isFromDialogueHistory: (file as any).isFromDialogueHistory,
          //   })),
        }),
        // onRehydrateStorage is called when the store is hydrated from storage
        onRehydrateStorage: () => {
          return (rehydratedState: any) => {
            if (rehydratedState) {
              // IMPORTANT: Clear modelId and modelVersionNum on rehydration
              // These can become stale if a model is deleted or if the user
              // switches between personas. Backend will use GeneralLLM slot.
              if (rehydratedState.config) {
                console.log(
                  "🔄 ChatStore: Clearing stale modelId on rehydration",
                  {
                    oldModelId: rehydratedState.config.modelId,
                    oldModelVersionNum: rehydratedState.config.modelVersionNum,
                  },
                );
                rehydratedState.config.modelId = null;
                rehydratedState.config.modelVersionNum = null;
                // Clear stale temperature/enable_thinking - these are now model defaults
                rehydratedState.config.chatControlMetadata = {};
              }

              // Rehydrated from sessionStorage
            }
          };
        },
      },
    ),
  ),
);

// ===========================
// Convenience Hooks
// ===========================

/**
 * Hook to get active datasets (computed from all sources)
 */
export const useActiveDatasets = () => {
  return useChatStore((state) => state.getActiveDatasets());
};

/**
 * Hook to get active dataset IDs
 */
export const useActiveDatasetIds = () => {
  return useChatStore((state) => state.getActiveDatasetIds());
};

/**
 * Hook to get source data record IDs for document-scoped search
 */
export const useSourceDataRecordIds = () => {
  return useChatStore((state) => state.getSourceDataRecordIds());
};

/**
 * Hook to get the active persona identity (routing > persona > none).
 * Single source of truth for persona display name across all components.
 */
export const useActivePersonaIdentity = () =>
  useChatStore((state) => state.getActivePersonaIdentity());

/**
 * Hook to check if current model supports vision/image inputs
 * Returns false by default if no model info available (safer to block than allow)
 * Note: Directly accesses state properties to ensure Zustand reactivity works correctly
 */
export const useSupportsVision = () => {
  return useChatStore((state) => {
    // Check persona modelCapabilities first
    if (state.persona?.modelCapabilities?.supportsVision !== undefined) {
      return state.persona.modelCapabilities.supportsVision;
    }
    // Check config modelCapabilities
    if (state.config?.modelCapabilities?.supportsVision !== undefined) {
      return state.config.modelCapabilities.supportsVision;
    }
    // Fall back to default model capabilities (GeneralLLM) if fetched
    if (state.defaultSupportsVision !== null) {
      return state.defaultSupportsVision;
    }
    // Default to false if no model info available
    return false;
  });
};
